import { CareSourceReviewContext } from './CareSourceByline';
import { getCareSourceAttribution } from './careSourceAttribution';
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import useOverflowToggle from '../../components/UI/useOverflowToggle';
import {
  getCareSourceDisplayStatus,
  getCareSourceOriginalText,
  formatCareSourceRecordedAt,
} from './careSourceStatusModel';
import {
  getDirectProfileReviewItems,
  getProfileChapterLabel,
} from './DirectProfileOrganizationReview';
import DirectProfileOrganizationReview from './DirectProfileOrganizationReview';
import CareSourceReviewableCandidateActions from './CareSourceReviewableCandidateActions';
import { buildReviewableCandidatesFromCareSource } from './careSourceReviewableCandidates';
import { getProfileSourceReviewSuggestions, getPrimaryProfileItems, isProfileSourceReviewComplete, buildProfileSavedSummary } from './profileSourceReviewSummary';
import { buildProfileSourceViewerModel } from '../profileContract/profileSourceViewer';

const getPrimaryChapterId = (source = {}) => (
  source.profileChapterId
  || source.sourceMetadata?.profileChapterId
  || source.metadata?.source?.profileChapterId
  || ''
);

const getItemText = (item = {}) => {
  const title = String(item.title || '').trim();
  const detail = String(item.content || item.summary || item.sourceText || '').trim();
  const titleWordCount = title.split(/\s+/).filter(Boolean).length;

  // Short extraction labels such as "Headphones" lose the caregiver-facing
  // outcome. Prefer the already-extracted fact text when it carries more meaning.
  if (detail && (!title || (titleWordCount <= 3 && detail !== title))) return detail;
  return title || detail;
};

const OriginalEntry = ({ source, completed = false }) => {
  const [expanded, setExpanded] = useState(false);
  const originalText = getCareSourceOriginalText(source);
  const {
    contentRef,
    expanded: textExpanded,
    hasOverflow,
    toggleExpanded,
  } = useOverflowToggle(originalText);
  const sourceModel = buildProfileSourceViewerModel({ source });
  const imageUrl = sourceModel.imageUrl || '';
  const isImage = Boolean(imageUrl);
  const originalExpanded = isImage ? expanded : textExpanded;
  const recordedAt = formatCareSourceRecordedAt(source);
  const authorName = getCareSourceAttribution(source).authorName || 'Author not recorded';
  const sourceKindLabel = `${authorName} · ${isImage ? 'Original image' : 'Original note'}`;
  const sourceLabel = recordedAt ? `${sourceKindLabel} · ${recordedAt}` : sourceKindLabel;
  const captureLink = source.id ? <Button component="a" href={`/sources/${source.id}`} size="small" sx={{ px: 0 }}>Open source note</Button> : null;
  if (!originalText && !imageUrl) return null;

  if (completed) {
    return (
      <Box>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: alpha(colors.semantic.ink, 0.52) }}>
          {sourceLabel}
        </Typography>
        {captureLink}
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{ px: 0, textTransform: 'none', fontWeight: 600 }}
        >
          {expanded
            ? `Hide original ${isImage ? 'image' : 'entry'}`
            : `View original ${isImage ? 'image' : 'entry'}`}
        </Button>
        <Collapse in={expanded}>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={sourceModel.title || 'Original source image'}
                style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 12 }}
              />
            ) : null}
            {originalText ? (
              <Typography sx={{ color: alpha(colors.semantic.ink, 0.72), whiteSpace: 'pre-wrap' }}>
                {originalText}
              </Typography>
            ) : null}
          </Stack>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: alpha(colors.semantic.ink, 0.58) }}>
        {sourceLabel}
      </Typography>
      {captureLink}
      {isImage && !expanded ? (
        <Box
          component="img"
          src={imageUrl}
          alt={sourceModel.title || 'Original source image'}
          sx={{ display: 'block', mt: 0.5, width: 72, height: 72, objectFit: 'cover', borderRadius: '10px' }}
        />
      ) : (
        <Typography
          ref={!isImage ? contentRef : undefined}
          sx={{
            mt: 0.35,
            color: alpha(colors.semantic.ink, 0.76),
            lineHeight: 1.45,
            display: originalExpanded ? 'block' : '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: originalExpanded ? 'unset' : 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
          }}
        >
          {isImage ? (
          <img
            src={imageUrl}
            alt={sourceModel.title || 'Original source image'}
            style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 260, objectFit: 'contain', borderRadius: 12 }}
          />
          ) : originalText}
        </Typography>
      )}
      {isImage ? (
        <>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            sx={{ mt: 0.25, px: 0, textTransform: 'none', fontWeight: 600 }}
          >
            {expanded ? 'Hide original image' : 'View original image'}
          </Button>
          {originalText ? (
            <Collapse in={expanded}>
              <Typography sx={{ mt: 0.35, color: alpha(colors.semantic.ink, 0.68), whiteSpace: 'pre-wrap' }}>
                {originalText}
              </Typography>
            </Collapse>
          ) : null}
        </>
      ) : (
        hasOverflow ? (
          <Button
            size="small"
            onClick={toggleExpanded}
            sx={{ mt: 0.25, px: 0, textTransform: 'none', fontWeight: 600 }}
          >
            {textExpanded ? 'Hide full entry' : 'View full entry'}
          </Button>
        ) : null
      )}
    </Box>
  );
};

const ProfileCareSourceReviewGroup = ({
  source = {},
  personName = '',
  onProfileReviewComplete,
  onVisibleCandidatesChange,
  visibleStructuredCandidates,
  existingMedications = [],
}) => {
  const suggestions = useMemo(
    () => getProfileSourceReviewSuggestions(source),
    [source]
  );
  const reviewItems = useMemo(
    () => getDirectProfileReviewItems(source, suggestions),
    [source, suggestions]
  );
  const destinationCandidates = useMemo(
    () => Array.isArray(visibleStructuredCandidates)
      ? visibleStructuredCandidates
      : buildReviewableCandidatesFromCareSource(source),
    [source, visibleStructuredCandidates]
  );
  const [completedResult, setCompletedResult] = useState(null);
  const primaryItems = getPrimaryProfileItems(source);
  const primaryItemLabels = primaryItems
    .map((item) => ({ item, text: getItemText(item) }))
    .filter(({ text }) => Boolean(text));
  const primaryChapterLabel = getProfileChapterLabel(getPrimaryChapterId(source));
  const complete = Boolean(completedResult)
    || isProfileSourceReviewComplete(source, reviewItems, destinationCandidates);
  const { addedCount, savedDestinationSummary } = buildProfileSavedSummary(
    source, reviewItems, completedResult, getProfileChapterLabel
  );
  const status = getCareSourceDisplayStatus(source).label;
  const processing = status === 'Processing' || status === 'Reading text';
  const currentPersonName = personName || source.childName || 'this person';

  const handleComplete = (result) => {
    setCompletedResult(result);
    onProfileReviewComplete?.(result);
  };

  if (complete) {
    return (
      <Paper variant="outlined" sx={{ px: 1.25, py: 1, borderRadius: '14px', boxShadow: 'none' }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 850, color: colors.semantic.ink }}>
          {`Review complete · ${savedDestinationSummary || `${addedCount} Profile detail${addedCount === 1 ? '' : 's'}`} saved`}
        </Typography>
        <OriginalEntry source={source} completed />
      </Paper>
    );
  }

  return (
    <Box sx={{ px: 0.25, py: 0.5 }}>
      <Stack spacing={0.9}>
        <OriginalEntry source={source} />

        {processing ? (
          <Typography sx={{ fontSize: '0.84rem', color: alpha(colors.semantic.ink, 0.62) }}>
            Organizing your latest update…
          </Typography>
        ) : null}

        {primaryItems.length ? (
          <Box>
            <Typography sx={{ fontWeight: 850 }}>
              {`${primaryItems.length} detail${primaryItems.length === 1 ? '' : 's'} added to ${primaryChapterLabel}`}
            </Typography>
            {primaryItemLabels.length ? (
              <Stack component="ul" spacing={0.45} sx={{ mt: 0.65, mb: 0, pl: 2.4 }}>
                {primaryItemLabels.map(({ item, text }, index) => (
                  <Typography component="li" key={item.id || item.clientId || index} sx={{ color: alpha(colors.semantic.ink, 0.78) }}>
                    {text}
                  </Typography>
                ))}
              </Stack>
            ) : null}
          </Box>
        ) : null}

        {reviewItems.length ? (
          <DirectProfileOrganizationReview
            source={source}
            suggestions={suggestions}
            personName={currentPersonName}
            onComplete={handleComplete}
          />
        ) : null}
        {destinationCandidates.length ? (
          <CareSourceReviewContext.Provider value={source}>
            <CareSourceReviewableCandidateActions
              source={source}
              candidates={destinationCandidates}
              hasGenericSuggestions={reviewItems.length > 0}
              onVisibleCandidatesChange={onVisibleCandidatesChange}
              existingMedications={existingMedications}
            />
          </CareSourceReviewContext.Provider>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ProfileCareSourceReviewGroup;
